import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import axios from "axios";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

type ProfessionWorkType =
  | "job"
  | "business"
  | "";

// ==========================
// SAVE / UPDATE PROFESSION DETAILS
// POST /user/profession/save
// ==========================
export const saveProfessionDetails =
  createAsyncThunk(
    "profession/saveProfessionDetails",
    async (
      payload: {
        user_id: number | string;
        work_type: ProfessionWorkType;

        job_title?: string;
        company_name?: string;
        job_department?: string;
        job_experience?: string;
        job_location?: string;
        job_description?: string;

        business_name?: string;
        business_category?: string;
        business_mobile?: string;
        business_email?: string;
        business_address?: string;
        business_website?: string;
        business_description?: string;
      },
      { rejectWithValue }
    ) => {
      try {
        const formData =
          new FormData();

        formData.append(
          "user_id",
          String(
            payload.user_id ||
              ""
          )
        );

        formData.append(
          "work_type",
          String(
            payload.work_type ||
              ""
          )
        );

        formData.append(
          "job_title",
          payload.job_title ||
            ""
        );

        formData.append(
          "company_name",
          payload.company_name ||
            ""
        );

        formData.append(
          "job_department",
          payload.job_department ||
            ""
        );

        formData.append(
          "job_experience",
          payload.job_experience ||
            ""
        );

        formData.append(
          "job_location",
          payload.job_location ||
            ""
        );

        formData.append(
          "job_description",
          payload.job_description ||
            ""
        );

        formData.append(
          "business_name",
          payload.business_name ||
            ""
        );

        formData.append(
          "business_category",
          payload.business_category ||
            ""
        );

        formData.append(
          "business_mobile",
          payload.business_mobile ||
            ""
        );

        formData.append(
          "business_email",
          payload.business_email ||
            ""
        );

        formData.append(
          "business_address",
          payload.business_address ||
            ""
        );

        formData.append(
          "business_website",
          payload.business_website ||
            ""
        );

        formData.append(
          "business_description",
          payload.business_description ||
            ""
        );

        const res =
          await axios.post(
            `${API_URL}/user/profession/save`,
            formData
          );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "Failed to save profession details"
        );
      }
    }
  );

// ==========================
// GET PROFESSION DETAILS
// ==========================
export const getProfessionDetails =
  createAsyncThunk(
    "profession/getProfessionDetails",
    async (
      user_id:
        | number
        | string,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${API_URL}/user/profession/get`,
            {
              params: {
                user_id,
              },
            }
          );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "Failed to fetch profession details"
        );
      }
    }
  );

interface ProfessionState {
  professionDetails:
    | any
    | null;

  loading: boolean;
  saveLoading: boolean;

  error: string | null;
  message: string | null;
}

const initialState: ProfessionState =
  {
    professionDetails: null,

    loading: false,
    saveLoading: false,

    error: null,
    message: null,
  };

const professionSlice =
  createSlice({
    name: "profession",

    initialState,

    reducers: {
      resetProfessionError: (
        state
      ) => {
        state.error = null;
      },

      resetProfessionMessage: (
        state
      ) => {
        state.message = null;
      },

      clearProfessionDetails: (
        state
      ) => {
        state.professionDetails =
          null;

        state.error = null;
        state.message = null;
      },
    },

    extraReducers: (
      builder
    ) => {
      builder
        .addCase(
          getProfessionDetails.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          getProfessionDetails.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.professionDetails =
              action.payload?.data ||
              null;

            state.message =
              action.payload?.message ||
              null;
          }
        )

        .addCase(
          getProfessionDetails.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.professionDetails =
              null;

            state.error =
              action.payload as string;
          }
        )

        .addCase(
          saveProfessionDetails.pending,
          (state) => {
            state.saveLoading =
              true;

            state.error = null;
            state.message = null;
          }
        )

        .addCase(
          saveProfessionDetails.fulfilled,
          (
            state,
            action
          ) => {
            state.saveLoading =
              false;

            state.message =
              action.payload?.message ||
              "Profession details saved successfully";

            state.professionDetails =
              action.payload?.data
                ?.details ||
              action.payload?.data ||
              null;
          }
        )

        .addCase(
          saveProfessionDetails.rejected,
          (
            state,
            action
          ) => {
            state.saveLoading =
              false;

            state.error =
              action.payload as string;
          }
        );
    },
  });

export const {
  resetProfessionError,
  resetProfessionMessage,
  clearProfessionDetails,
} = professionSlice.actions;

export default professionSlice.reducer;
